import {escapeHtml} from "../services/translations";
import {getTranslation} from "./helpers";
import utils from "../utils/utils.js";
import {is_auth_or_permission_failure, is_wp_ajax_nonce_failure} from "../utils/runtime-credentials";
import {clearCloudAuthFromSession, readCloudAuthFromSession, writeCloudAuthToSession} from "./cloud-auth-storage";
import {getSiteVar} from "../services/site-vars";
import {userMessage} from "./user-message";

const leadPopupAuthAction = 'wl_get_lead_popup_cloud_auth';
const leadPopupAuthRefreshSkewMs = 60 * 1000;
const leadPopupCloudTimeoutMs = 30 * 1000;

let leadPopupCloudAuth = null;
let leadPopupCloudAuthPromise = null;

function createLeadPopupCloudError(message, code = 'lead_popup_cloud_error', extra = {}) {
    const error = new Error(message);
    error.code = code;
    Object.assign(error, extra);
    return error;
}

function ajaxPromise(ajaxSettings = {}) {
    return new Promise((resolve, reject) => {
        $.ajax({
            ...ajaxSettings,
            success: (response, textStatus, jqXHR) => resolve({response, textStatus, jqXHR}),
            error: (xhr, status, error) => reject({xhr, status, error}),
        });
    });
}

function normalizeStoredLeadPopupAuth(authPayload = null) {
    if (!authPayload || typeof authPayload !== 'object') {
        return null;
    }

    const functionUrl = String(authPayload.functionUrl || '').trim();
    const authToken = String(authPayload.authToken || '').trim();
    const expiresAtMs = Number.parseInt(authPayload.expiresAtMs, 10) || 0;
    if (!functionUrl || !authToken || expiresAtMs <= 0) {
        return null;
    }

    return {
        enabled: true,
        functionUrl,
        authToken,
        expiresAtMs,
    };
}

function normalizeLeadPopupAuthResponse(response = null) {
    if (!response || response.success !== true || !response.data || typeof response.data !== 'object') {
        return null;
    }

    const functionUrl = String(response.data.function_url || '').trim();
    const authToken = String(response.data.auth_token || '').trim();
    const expiresAt = Number.parseInt(response.data.expires_at, 10) || 0;
    if (!functionUrl || !authToken || expiresAt <= 0) {
        return null;
    }

    return {
        enabled: true,
        functionUrl,
        authToken,
        expiresAtMs: expiresAt * 1000,
    };
}

function getLeadPopupAuthContextKey() {
    return [
        'lead_popup',
        Number.parseInt(getSiteVar('current_user_id'), 10) || 0,
    ].join(':');
}

function hasFreshLeadPopupAuth(authPayload = null) {
    const expiresAtMs = Number.parseInt(authPayload?.expiresAtMs, 10) || 0;
    if (!authPayload || !authPayload.authToken || !authPayload.functionUrl || expiresAtMs <= 0) {
        return false;
    }

    return expiresAtMs > (Date.now() + leadPopupAuthRefreshSkewMs);
}

function clearLeadPopupAuth() {
    leadPopupCloudAuth = null;
    leadPopupCloudAuthPromise = null;
    clearCloudAuthFromSession({
        bootstrapAction: leadPopupAuthAction,
        contextKey: getLeadPopupAuthContextKey(),
    });
}

async function fetchLeadPopupCloudAuth(forceRefresh = false) {
    if (forceRefresh) {
        clearLeadPopupAuth();
    }

    if (!leadPopupCloudAuth) {
        leadPopupCloudAuth = readCloudAuthFromSession({
            bootstrapAction: leadPopupAuthAction,
            contextKey: getLeadPopupAuthContextKey(),
            normalizeAuthPayload: normalizeStoredLeadPopupAuth,
        });
    }

    if (hasFreshLeadPopupAuth(leadPopupCloudAuth)) {
        return leadPopupCloudAuth;
    }

    if (leadPopupCloudAuthPromise) {
        return leadPopupCloudAuthPromise;
    }

    const requestAuth = async () => {
        const {response} = await ajaxPromise({
            url: utils.get_ajax_url(),
            type: 'POST',
            dataType: 'json',
            wl_skip_nonce_retry: true,
            data: {
                action: leadPopupAuthAction,
                _wpnonce: utils.get_nonce(),
            },
        });

        const authPayload = normalizeLeadPopupAuthResponse(response);
        if (!authPayload) {
            throw createLeadPopupCloudError(
                userMessage(response?.data) || 'Failed to fetch lead popup Cloud auth.',
                'lead_popup_cloud_bootstrap_failed',
            );
        }

        leadPopupCloudAuth = authPayload;
        writeCloudAuthToSession({
            bootstrapAction: leadPopupAuthAction,
            contextKey: getLeadPopupAuthContextKey(),
            authPayload,
            normalizeAuthPayload: normalizeStoredLeadPopupAuth,
        });
        return authPayload;
    };

    leadPopupCloudAuthPromise = (async () => {
        try {
            return await requestAuth();
        } catch (err) {
            if (is_wp_ajax_nonce_failure(err)) {
                await utils.fetch_fresh_nonce(true);
                return requestAuth();
            }

            if (is_auth_or_permission_failure(err)) {
                clearLeadPopupAuth();
            }

            throw err;
        } finally {
            leadPopupCloudAuthPromise = null;
        }
    })();

    return leadPopupCloudAuthPromise;
}

function normalizeLeadPopupHtmlResponse(response = null) {
    if (!response || response.success !== true || typeof response.html !== 'string' || response.html.trim() === '') {
        throw createLeadPopupCloudError(
            response?.message || 'Lead popup Cloud response was invalid.',
            'lead_popup_cloud_invalid_response',
        );
    }

    return response.html;
}

async function requestLeadPopupCloudHtmlWithAuth(data = {}, authPayload) {
    const firebaseId = String(data.firebase_id || '').trim();
    if (!firebaseId) {
        throw createLeadPopupCloudError('Missing lead id.', 'lead_popup_cloud_missing_firebase_id');
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), leadPopupCloudTimeoutMs);
    let response = null;
    let payload = {};

    try {
        response = await fetch(authPayload.functionUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authPayload.authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                firebase_id: firebaseId,
            }),
            signal: controller.signal,
        });
        payload = await response.json().catch(() => ({}));
    } finally {
        window.clearTimeout(timeoutId);
    }

    if (!response.ok) {
        const error = createLeadPopupCloudError(
            payload?.message || getTranslation('lead_load_error'),
            response.status === 401 || response.status === 403 ? 'lead_popup_cloud_unauthorized' : 'lead_popup_cloud_request_failed',
            {status: response.status},
        );
        throw error;
    }

    return normalizeLeadPopupHtmlResponse(payload);
}

export async function requestLeadPopupCloudHtml(data = {}) {
    let authPayload = await fetchLeadPopupCloudAuth(false);

    try {
        return await requestLeadPopupCloudHtmlWithAuth(data, authPayload);
    } catch (err) {
        if (err?.status !== 401 && err?.status !== 403) {
            throw err;
        }

        authPayload = await fetchLeadPopupCloudAuth(true);
        return requestLeadPopupCloudHtmlWithAuth(data, authPayload);
    }
}

export function renderLeadPopupCloudErrorHtml() {
    return '<div class="lead-info-information"><div><p class="header">' + escapeHtml(getTranslation('lead_information')) + '</p><p class="value">' + escapeHtml(getTranslation('lead_load_error')) + '</p></div></div>';
}
